/**
 * EmpAI linked product picker — navigates to sibling products by
 * color / thickness / size / quantity (from empai.linking JSON).
 * swatch-map: 2026-08-10-acrylic-color-v3
 */
(function () {
  function norm(v) {
    return String(v || "")
      .trim()
      .toLowerCase();
  }

  function packQty(v) {
    var n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  // Solid hex preferred for 22px dots (washes/gradients read as gray on storefront).
  // Keys are looked up after normalizing hyphens → spaces.
  var SWATCHES = {
    silver: "#C0C0C0",
    gold: "#D4AF37",
    "rose-gold": "#B76E79",
    "rose gold": "#B76E79",
    "iridescent-rainbow":
      "linear-gradient(135deg, #60CFFF 0%, #C154F0 35%, #D16BFF 65%, #7B2CBF 100%)",
    "iridescent rainbow":
      "linear-gradient(135deg, #60CFFF 0%, #C154F0 35%, #D16BFF 65%, #7B2CBF 100%)",
    clear:
      "linear-gradient(135deg, rgba(200,230,255,0.35) 0%, rgba(255,255,255,0.9) 55%, rgba(180,210,240,0.45) 100%)",
    matte: "#d8d8d8",
    "matte clear":
      "linear-gradient(135deg, #e8eef3 0%, #f7f7f7 50%, #dce3ea 100%)",
    frosted: "linear-gradient(135deg, #e6e6e6 0%, #f4f4f4 45%, #cfcfcf 100%)",
    "white opaque": "#f5f5f5",
    "white medium translucent": "#f0f0f0",
    "white high translucent": "#fafafa",
    black: "#111111",
    white: "#f5f5f5",
    blue: "#1c7ed6",
    green: "#2f9e44",
    orange: "#f76707",
    yellow: "#f59f00",
    red: "#e03131",
    pink: "#e64980",
    purple: "#7048e8",
    gray: "#868e96",
    grey: "#868e96",
    bronze: "#a8793d",
    "bronze translucent": "#c49a6c",
    "bronze transparent": "#d4b896",
    "gray translucent": "#adb5bd",
    "grey translucent": "#adb5bd",
    "gray transparent": "#ced4da",
    "grey transparent": "#ced4da",
    "fluorescent blue": "#15aabf",
    "fluorescent green": "#40c057",
    "fluorescent orange": "#fd7e14",
    "fluorescent red": "#fa5252",
    "brushed silver":
      "linear-gradient(135deg, #9aa0a6 0%, #e8eaed 45%, #9aa0a6 100%)",
    "brushed gold":
      "linear-gradient(135deg, #b8860b 0%, #f0e6c8 45%, #b8860b 100%)",
  };

  function swatchKey(colorName) {
    return norm(colorName)
      .replace(/[_/]+/g, " ")
      .replace(/-/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function swatchBg(colorName) {
    var key = swatchKey(colorName);
    return SWATCHES[key] || SWATCHES[norm(colorName)] || "#d0d0d0";
  }

  function applySwatchDot(dot, colorName) {
    var bg = swatchBg(colorName);
    // Prefer background-color for solids so theme CSS cannot wash them out.
    if (bg.indexOf("gradient") >= 0) {
      dot.style.setProperty("background", bg, "important");
      dot.style.setProperty("background-color", "transparent", "important");
    } else {
      dot.style.setProperty("background", bg, "important");
      dot.style.setProperty("background-color", bg, "important");
    }
    dot.setAttribute("data-swatch-bg", bg);
    var cKey = swatchKey(colorName);
    if (
      cKey === "white" ||
      cKey.indexOf("clear") >= 0 ||
      cKey.indexOf("frost") >= 0 ||
      cKey.indexOf("white") === 0 ||
      cKey.indexOf("transparent") >= 0 ||
      cKey === "yellow" ||
      cKey === "bronze transparent" ||
      cKey === "gray transparent" ||
      cKey === "grey transparent"
    ) {
      dot.classList.add("is-light");
    }
  }

  function formatColorLabel(color) {
    var key = norm(color).replace(/-/g, " ");
    if (key === "matte clear") return "Matte Clear (Frosted)";
    if (!color) return "";
    return String(color)
      .replace(/-/g, " ")
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function formatSizeLabel(size) {
    return String(size || "").replace(/[x×]/gi, " × ");
  }

  /** Tag values like 1-8 → 1/8; decimal tags like 0-080 → 0.08 (not 0/080). */
  function formatThicknessLabel(thickness) {
    var raw = String(thickness || "").trim();
    if (!raw) return "";
    var decimal = raw.match(/^0-(\d+)$/i);
    if (decimal) {
      var asNum = Number("0." + decimal[1]);
      if (Number.isFinite(asNum) && asNum > 0) {
        return String(asNum);
      }
    }
    return raw.replace(/-/g, "/");
  }

  function formatQtyLabel(qty) {
    var n = packQty(qty);
    return n <= 1 ? "1 (Single)" : "Set of " + n;
  }

  function parseLinking(root) {
    var raw = root.getAttribute("data-empai-linking");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function findSibling(siblings, color, thickness, size, quantity) {
    var exact = siblings.filter(function (s) {
      return (
        norm(s.color) === norm(color) &&
        norm(s.thickness) === norm(thickness) &&
        norm(s.size) === norm(size)
      );
    });
    if (exact.length) {
      var sameQty = exact.find(function (s) {
        return packQty(s.quantity) === packQty(quantity);
      });
      if (sameQty) return sameQty;
      var single = exact.find(function (s) {
        return packQty(s.quantity) === 1;
      });
      return single || exact[0];
    }
    var soft = siblings.filter(function (s) {
      return (
        norm(s.color) === norm(color) && norm(s.thickness) === norm(thickness)
      );
    });
    return (
      soft.find(function (s) {
        return packQty(s.quantity) === packQty(quantity);
      }) ||
      soft.find(function (s) {
        return packQty(s.quantity) === 1;
      }) ||
      soft[0] ||
      siblings.find(function (s) {
        return norm(s.color) === norm(color);
      }) ||
      null
    );
  }

  function go(sibling, currentHandle) {
    if (!sibling || !sibling.handle) return;
    if (norm(sibling.handle) === norm(currentHandle)) return;
    var url = sibling.url || "/products/" + sibling.handle;
    window.location.href = url;
  }

  function formatMoney(cents, moneyFormat) {
    var amount = (Number(cents) || 0) / 100;
    var formatted = amount.toFixed(2);
    var fmt = moneyFormat || "${{amount}}";
    if (fmt.indexOf("{{amount_no_decimals}}") !== -1) {
      return fmt.replace("{{amount_no_decimals}}", String(Math.round(amount)));
    }
    if (fmt.indexOf("{{amount_with_comma_separator}}") !== -1) {
      return fmt.replace(
        "{{amount_with_comma_separator}}",
        formatted.replace(".", ","),
      );
    }
    return fmt
      .replace("{{amount}}", formatted)
      .replace("{{amount_with_space_separator}}", formatted);
  }

  /** Set sizes shown in the savings table (matches Quantity set dropdown). */
  var SET_TIERS = [1, 2, 3, 5, 7, 10];

  function findPackSibling(siblings, state, qty) {
    return (
      (siblings || []).find(function (s) {
        return (
          norm(s.color) === norm(state.color) &&
          norm(s.thickness) === norm(state.thickness) &&
          norm(s.size) === norm(state.size) &&
          packQty(s.quantity) === packQty(qty)
        );
      }) || null
    );
  }

  function fetchProductJson(handle) {
    return fetch("/products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
    }).then(function (res) {
      if (!res.ok) throw new Error("product fetch failed");
      return res.json();
    });
  }

  function fetchProductPriceCents(handle) {
    return fetchProductJson(handle)
      .then(function (json) {
        var price = json && json.price != null ? Number(json.price) : NaN;
        // Storefront .js returns cents as integer
        if (Number.isFinite(price)) return Math.round(price);
        var v0 = json && json.variants && json.variants[0];
        var vp = v0 && v0.price != null ? Number(v0.price) : NaN;
        return Number.isFinite(vp) ? Math.round(vp) : null;
      })
      .catch(function () {
        return null;
      });
  }

  function resolveSiblingPriceCents(sibling, currentHandle, displayedCents) {
    if (!sibling) return Promise.resolve(null);
    if (
      sibling.priceCents != null &&
      Number.isFinite(Number(sibling.priceCents))
    ) {
      return Promise.resolve(Number(sibling.priceCents));
    }
    // Current product: use displayed price box (source of truth)
    if (norm(sibling.handle) === norm(currentHandle) && displayedCents > 0) {
      return Promise.resolve(displayedCents);
    }
    return fetchProductPriceCents(sibling.handle);
  }

  function renderVolumeTable(root, data, state) {
    var box = root.querySelector('[data-role="volume-discount"]');
    if (!box) return;
    var priceEl = root.querySelector('[data-role="price-value"]');
    var displayedCents = Number(
      (priceEl && priceEl.getAttribute("data-price-cents")) ||
        root.getAttribute("data-price-cents") ||
        0,
    );
    var moneyFormat = root.getAttribute("data-money-format") || "${{amount}}";
    var currentHandle = root.getAttribute("data-product-handle") || "";
    var qtyRow = box.querySelector('[data-role="volume-qty-row"]');
    var pctRow = box.querySelector('[data-role="volume-pct-row"]');
    var priceRow = box.querySelector('[data-role="volume-price-row"]');
    var baseLabel = box.querySelector('[data-role="volume-base-price"]');
    if (!qtyRow || !pctRow || !priceRow) return;

    if (baseLabel && displayedCents > 0) {
      baseLabel.textContent = formatMoney(displayedCents, moneyFormat);
    }

    var packs = SET_TIERS.map(function (qty) {
      return {
        qty: qty,
        sibling: findPackSibling(data.siblings, state, qty),
      };
    }).filter(function (row) {
      return row.sibling;
    });

    if (!packs.length) {
      box.hidden = true;
      return;
    }
    box.hidden = false;

    Promise.all(
      packs.map(function (row) {
        return resolveSiblingPriceCents(
          row.sibling,
          currentHandle,
          displayedCents,
        ).then(function (cents) {
          return { qty: row.qty, sibling: row.sibling, cents: cents };
        });
      }),
    ).then(function (rows) {
      var single = rows.find(function (r) {
        return r.qty === 1 && r.cents > 0;
      });
      var singleCents = single ? single.cents : 0;

      qtyRow.innerHTML = '<th scope="row">Quantity</th>';
      pctRow.innerHTML = '<th scope="row">Discount</th>';
      priceRow.innerHTML = '<th scope="row">Price</th>';

      rows.forEach(function (row) {
        var th = document.createElement("th");
        th.scope = "col";
        th.textContent = row.qty <= 1 ? "1 (Single)" : "Set of " + row.qty;
        if (packQty(state.quantity) === row.qty) {
          th.className = "is-current";
        }
        qtyRow.appendChild(th);

        var tdPct = document.createElement("td");
        var tdPrice = document.createElement("td");
        if (packQty(state.quantity) === row.qty) {
          tdPct.className = "is-current";
          tdPrice.className = "is-current";
        }

        if (!row.cents) {
          tdPct.textContent = "—";
          tdPrice.textContent = "—";
        } else if (row.qty <= 1 || !singleCents) {
          tdPct.textContent = "—";
          tdPrice.innerHTML =
            formatMoney(row.cents, moneyFormat) +
            ' <span class="empai-volume-discount__each">' +
            (row.qty <= 1 ? "/each" : "/set") +
            "</span>";
        } else {
          var full = singleCents * row.qty;
          var savePct =
            full > 0 ? Math.round(((full - row.cents) / full) * 100) : 0;
          if (savePct < 0) savePct = 0;
          tdPct.textContent = savePct > 0 ? savePct + "%" : "—";
          tdPrice.innerHTML =
            formatMoney(row.cents, moneyFormat) +
            ' <span class="empai-volume-discount__each">/set</span>';
        }
        pctRow.appendChild(tdPct);
        priceRow.appendChild(tdPrice);
      });
    });
  }

  function init(root) {
    var data = parseLinking(root);
    if (!data || !Array.isArray(data.siblings) || !data.siblings.length) return;

    var currentHandle = root.getAttribute("data-product-handle") || "";
    var state = {
      color: data.color || "",
      thickness: data.thickness || "",
      size: data.size || "",
      quantity: packQty(data.quantity),
    };

    var showColor = root.getAttribute("data-show-color") !== "false";
    var showThickness = root.getAttribute("data-show-thickness") !== "false";
    var showSize = root.getAttribute("data-show-size") !== "false";
    var showQuantity = root.getAttribute("data-show-quantity") !== "false";

    var colorsEl = root.querySelector('[data-role="colors"]');
    var thicknessEl = root.querySelector('[data-role="thickness"]');
    var sizeEl = root.querySelector('[data-role="size"]');
    var qtyEl = root.querySelector('[data-role="quantity"]');
    var atcBtn = root.querySelector('[data-role="add-to-cart"]');
    var cartQtyEl = root.querySelector('[data-role="cart-quantity"]');
    var statusEl = root.querySelector('[data-role="status"]');

    function readCartQuantity() {
      var n = cartQtyEl ? Number(cartQtyEl.value) : 1;
      if (!Number.isFinite(n) || n < 1) n = 1;
      n = Math.floor(n);
      if (cartQtyEl) cartQtyEl.value = String(n);
      return n;
    }

    function setStatus(msg, isError, opts) {
      if (!statusEl) return;
      statusEl.replaceChildren();
      if (!msg) {
        statusEl.hidden = true;
        statusEl.classList.remove("is-error");
        return;
      }
      statusEl.hidden = false;
      statusEl.classList.toggle("is-error", Boolean(isError));
      var href = opts && opts.cartHref;
      if (!isError && href) {
        var a = document.createElement("a");
        a.href = href;
        a.className = "empai-linked-picker__status-link";
        a.setAttribute("aria-label", "View cart");
        var label = document.createElement("span");
        label.textContent = msg;
        var cta = document.createElement("span");
        cta.className = "empai-linked-picker__status-cta";
        cta.textContent = "View cart →";
        a.appendChild(label);
        a.appendChild(cta);
        statusEl.appendChild(a);
        return;
      }
      statusEl.textContent = msg;
    }

    function refreshCartCount(cart) {
      if (!cart || typeof cart.item_count !== "number") return;
      var countEls = document.querySelectorAll(".cart-count, .cart-link__bubble, [data-cart-count]");
      countEls.forEach(function (el) {
        el.textContent = String(cart.item_count);
        if (cart.item_count > 0) el.classList.remove("hide", "hidden");
      });
    }

    function loadPackOptions() {
      return Promise.all(
        SET_TIERS.map(function (qty) {
          var sib = findPackSibling(data.siblings, state, qty);
          if (!sib) return Promise.resolve(null);
          return fetchProductJson(sib.handle)
            .then(function (json) {
              var variantId =
                json && json.variants && json.variants[0]
                  ? Number(json.variants[0].id)
                  : 0;
              var priceCents =
                json && json.price != null
                  ? Math.round(Number(json.price))
                  : null;
              if (
                (priceCents == null || !Number.isFinite(priceCents)) &&
                sib.priceCents != null
              ) {
                priceCents = Number(sib.priceCents);
              }
              if (!variantId || !priceCents) return null;
              return {
                qty: qty,
                handle: sib.handle,
                variantId: variantId,
                priceCents: priceCents,
              };
            })
            .catch(function () {
              return null;
            });
        }),
      ).then(function (rows) {
        return rows.filter(Boolean);
      });
    }

    /** Cheapest mix of set SKUs for N single sheets. */
    function optimizePackPlan(sheetCount, packs) {
      var n = Math.max(0, Math.floor(Number(sheetCount) || 0));
      if (!n || !packs.length) return [];
      var dp = [];
      var choice = [];
      var i;
      for (i = 0; i <= n; i++) {
        dp[i] = Infinity;
        choice[i] = null;
      }
      dp[0] = 0;
      for (i = 1; i <= n; i++) {
        packs.forEach(function (p) {
          if (p.qty <= i && dp[i - p.qty] + p.priceCents < dp[i]) {
            dp[i] = dp[i - p.qty] + p.priceCents;
            choice[i] = p;
          }
        });
      }
      if (!Number.isFinite(dp[n]) || !choice[n]) return [];
      var plan = [];
      var left = n;
      while (left > 0 && choice[left]) {
        plan.push(choice[left]);
        left -= choice[left].qty;
      }
      return plan;
    }

    function collapsePlan(plan) {
      var map = {};
      plan.forEach(function (p) {
        var key = String(p.variantId);
        if (!map[key]) {
          map[key] = {
            id: p.variantId,
            quantity: 0,
            qty: p.qty,
            handle: p.handle,
            priceCents: p.priceCents,
          };
        }
        map[key].quantity += 1;
      });
      return Object.keys(map).map(function (k) {
        return map[k];
      });
    }

    function describePlan(items) {
      return items
        .map(function (it) {
          var label = it.qty <= 1 ? "Single" : "Set of " + it.qty;
          return (it.quantity > 1 ? it.quantity + "× " : "") + label;
        })
        .join(", ");
    }

    function familyHandles(packs) {
      var set = {};
      (packs || []).forEach(function (p) {
        if (p && p.handle) set[norm(p.handle)] = true;
      });
      (data.siblings || []).forEach(function (s) {
        if (
          norm(s.color) === norm(state.color) &&
          norm(s.thickness) === norm(state.thickness) &&
          norm(s.size) === norm(state.size) &&
          s.handle
        ) {
          set[norm(s.handle)] = true;
        }
      });
      return set;
    }

    function sheetsInCartForFamily(cart, packs) {
      var handles = familyHandles(packs);
      var sheets = 0;
      var keys = [];
      (cart.items || []).forEach(function (item) {
        if (!item || !item.handle || !handles[norm(item.handle)]) return;
        if (item.properties && Object.keys(item.properties).length) return;
        var qty = packQty(
          (packs.find(function (p) {
            return norm(p.handle) === norm(item.handle);
          }) || {}).qty || 1,
        );
        // Prefer pack size from handle suffix when present
        var m = String(item.handle).match(/-pack-(\d+)$/i);
        if (m) qty = Number(m[1]) || qty;
        sheets += qty * (Number(item.quantity) || 0);
        if (item.key) keys.push(item.key);
      });
      return { sheets: sheets, keys: keys };
    }

    function addStandardToCart() {
      if (!atcBtn) return;
      var available = root.getAttribute("data-available") !== "false";
      var cartQty = readCartQuantity();
      var addingSheets = packQty(state.quantity) * cartQty;
      if (!available || atcBtn.disabled || addingSheets < 1) return;

      atcBtn.disabled = true;
      var prev = atcBtn.textContent;
      atcBtn.textContent = "Adding…";
      setStatus("");

      loadPackOptions()
        .then(function (packs) {
          if (!packs.length) {
            throw new Error("No pack prices available");
          }
          return fetch("/cart.js", {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
          })
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              var existing = sheetsInCartForFamily(cart, packs);
              var sheetCount = existing.sheets + addingSheets;
              var plan = optimizePackPlan(sheetCount, packs);
              var items = collapsePlan(plan);
              if (!items.length) {
                throw new Error("Could not build cart items");
              }

              var clear =
                existing.keys.length > 0
                  ? fetch("/cart/update.js", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                      },
                      credentials: "same-origin",
                      body: JSON.stringify({
                        updates: existing.keys.reduce(function (acc, key) {
                          acc[key] = 0;
                          return acc;
                        }, {}),
                      }),
                    }).then(function (res) {
                      return res.json();
                    })
                  : Promise.resolve(null);

              return clear.then(function () {
                return fetch("/cart/add.js", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                  body: JSON.stringify({
                    items: items.map(function (it) {
                      return { id: it.id, quantity: it.quantity };
                    }),
                  }),
                  credentials: "same-origin",
                }).then(function (res) {
                  return res.json().then(function (body) {
                    if (!res.ok) {
                      throw new Error(
                        (body && (body.description || body.message)) ||
                          "Could not add to cart",
                      );
                    }
                    return { body: body, items: items, sheetCount: sheetCount };
                  });
                });
              });
            });
        })
        .then(function (result) {
          return fetch("/cart.js", {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
          })
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              return { cart: cart, items: result.items };
            });
        })
        .then(function (result) {
          refreshCartCount(result.cart);
          setStatus("Cart updated → " + describePlan(result.items), false, {
            cartHref: root.getAttribute("data-cart-url") || "/cart",
          });
          atcBtn.textContent = "Added ✓";
          setTimeout(function () {
            atcBtn.textContent = prev || "Add to cart";
            atcBtn.disabled = root.getAttribute("data-available") === "false";
          }, 1800);
          document.dispatchEvent(
            new CustomEvent("empai:standard-added-to-cart", {
              detail: { cart: result.cart, items: result.items },
            }),
          );
        })
        .catch(function (err) {
          setStatus(err && err.message ? err.message : "Could not add to cart", true);
          atcBtn.textContent = prev || "Add to cart";
          atcBtn.disabled = false;
        });
    }

    if (cartQtyEl) {
      cartQtyEl.addEventListener("change", readCartQuantity);
      cartQtyEl.addEventListener("blur", readCartQuantity);
    }
    if (atcBtn) {
      atcBtn.addEventListener("click", addStandardToCart);
    }

    function availableColor(c) {
      return data.siblings.some(function (s) {
        return (
          norm(s.color) === norm(c) &&
          (!state.thickness || norm(s.thickness) === norm(state.thickness)) &&
          (!state.size || norm(s.size) === norm(state.size))
        );
      });
    }

    function renderColors() {
      var colors = data.colors || [];
      var group = colorsEl && colorsEl.closest(".empai-linked-picker__group");
      if (!colorsEl || !group) return;
      if (!colors.length) {
        group.hidden = true;
        return;
      }
      group.hidden = false;
      colorsEl.innerHTML = "";
      colors.forEach(function (c) {
        var selected = norm(c) === norm(state.color);
        var available = availableColor(c);
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className =
          "empai-linked-picker__swatch" +
          (selected ? " is-selected" : "") +
          (!available && !selected ? " is-disabled" : "");
        btn.title = formatColorLabel(c);
        btn.setAttribute("data-color", c);
        btn.disabled = !available && !selected;
        var dot = document.createElement("span");
        dot.className = "empai-linked-picker__dot";
        applySwatchDot(dot, c);
        btn.appendChild(dot);
        btn.appendChild(document.createTextNode(formatColorLabel(c)));
        btn.addEventListener("click", function () {
          navigate(c, state.thickness, state.size, state.quantity);
        });
        colorsEl.appendChild(btn);
      });
    }

    function fillSelect(select, values, formatter, current) {
      var group = select && select.closest(".empai-linked-picker__group");
      if (!select || !group) return;
      if (!values || !values.length) {
        group.hidden = true;
        return;
      }
      group.hidden = false;
      select.innerHTML = "";
      values.forEach(function (v) {
        var opt = document.createElement("option");
        opt.value = String(v);
        opt.textContent = formatter(v);
        if (norm(v) === norm(current) || String(v) === String(current)) {
          opt.selected = true;
        }
        select.appendChild(opt);
      });
    }

    function navigate(color, thickness, size, quantity) {
      var match = findSibling(
        data.siblings,
        color,
        thickness,
        size,
        quantity,
      );
      if (!match) return;
      if (norm(match.handle) === norm(currentHandle)) {
        state.color = color;
        state.thickness = thickness;
        state.size = size;
        state.quantity = packQty(quantity);
        render();
        return;
      }
      go(match, currentHandle);
    }

    function render() {
      if (showColor) renderColors();
      if (showThickness) {
        fillSelect(
          thicknessEl,
          data.thicknesses || [],
          formatThicknessLabel,
          state.thickness,
        );
      }
      if (showSize) {
        fillSelect(sizeEl, data.sizes || [], formatSizeLabel, state.size);
      }
      if (showQuantity) {
        fillSelect(
          qtyEl,
          data.quantities || [],
          formatQtyLabel,
          state.quantity,
        );
      }
      // Set prices from sibling SKUs; % = savings vs single × qty
      renderVolumeTable(root, data, state);
    }

    if (thicknessEl) {
      thicknessEl.addEventListener("change", function () {
        var next = thicknessEl.value;
        var sizeForNext =
          (
            data.siblings.find(function (s) {
              return (
                norm(s.color) === norm(state.color) &&
                norm(s.thickness) === norm(next) &&
                norm(s.size) === norm(state.size) &&
                packQty(s.quantity) === packQty(state.quantity)
              );
            }) ||
            data.siblings.find(function (s) {
              return (
                norm(s.color) === norm(state.color) &&
                norm(s.thickness) === norm(next)
              );
            }) ||
            {}
          ).size || state.size;
        navigate(state.color, next, sizeForNext, state.quantity);
      });
    }
    if (sizeEl) {
      sizeEl.addEventListener("change", function () {
        navigate(state.color, state.thickness, sizeEl.value, state.quantity);
      });
    }
    if (qtyEl) {
      qtyEl.addEventListener("change", function () {
        navigate(
          state.color,
          state.thickness,
          state.size,
          Number(qtyEl.value) || 1,
        );
      });
    }

    render();
  }

  function boot() {
    var root = document.getElementById("EmpaiLinkedPicker");
    if (root) init(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
